// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { METRES_PER_INTERNATIONAL_FOOT, METRES_PER_US_SURVEY_FOOT } from 'Utils/constants';

export type CadUnits = 'none' | 'metre' | 'foot' | 'US survey foot' | 'degree';

/**
 * Which unit convention an order's CAD deliverables use, so a display toggle can follow the
 * order's own data instead of assuming one. Mirrors the one existing precedent for this
 * (EsriSplitViewer/index.tsx's orderUnits lookup): there is no server-side validation forcing
 * every cadFile in an order to agree, so this takes the first cadFile that has units set.
 * @returns the order's CAD units, or null when none of its cadFiles have one set.
 */
export const resolveOrderCadUnits = (
  cadFiles: { units?: CadUnits }[] | undefined | null,
): CadUnits | null => {
  if (!Array.isArray(cadFiles)) return null;
  const withUnits = cadFiles.find((cad) => cad.units);
  return withUnits?.units ?? null;
};

/**
 * Feet per metre for the order's own foot convention. Defaults to the international foot — the
 * common, colloquial "feet" — for orders with no CAD units set, or units that are not feet-based.
 * @returns the factor to multiply a metres value by to get feet.
 */
export const resolveFeetPerMetre = (cadUnits: CadUnits | null): number =>
  1 / (cadUnits === 'US survey foot' ? METRES_PER_US_SURVEY_FOOT : METRES_PER_INTERNATIONAL_FOOT);

// Every elevation offset is stored in metres; this is the display unit a reader can switch that
// stored value into.
export type DisplayUnit = 'm' | 'ft';

/** Converts a metres-native value into the given display unit. */
export const toDisplayValue = (valueMetres: number, unit: DisplayUnit, feetPerMetre: number): number =>
  (unit === 'ft' ? valueMetres * feetPerMetre : valueMetres);

/** Inverse of toDisplayValue — converts a value shown/typed in the given unit back to metres. */
export const fromDisplayValue = (displayValue: number, unit: DisplayUnit, feetPerMetre: number): number =>
  (unit === 'ft' ? displayValue / feetPerMetre : displayValue);
